/**
 * Modal for capturing auth-status reasons, comments, and preferred-drug
 * context. Denial/query reasons are staged in UserStore pending remote
 * bucket persistence.
 * @module StatusUpdateModal
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";

import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
// import { DENIAL_QUERY_BUCKET_LIMIT } from "../../../data-model/denialQueryBucket";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { useStatusUpdateFormState } from "./hooks/useStatusUpdateFormState";
import type { StatusUpdateModalProps } from "./statusUpdateModal.types";
import StatusUpdateModalBody from "./StatusUpdateModalBody";
import { createDataForCommentsAndPushToLocalStorage } from "./utils/createDataForCommentsAndPushToLocalStorage";
import { upsertDenialQueryReasonInStore } from "./utils/denialQueryReasonUserStoreHelper";
import { isUserInputRequired } from "./utils/isUserInputRequired";
import { shouldShowAdditionalInformationForQueryReason } from "./utils/shouldShowAdditionalInformationForQueryReason";
// import { saveDenialOrQueryReason } from "./utils/saveDenialOrQueryReason";
// import { getLocalDenialQueryBucketTimeContext } from "../../../utils/formatDate";
import "./StatusUpdateModal.scss";

export type {
  AuthStatusChange,
  PreferredDrugFormState,
  PreferredJcodeEntry,
} from "./statusUpdateModal.types";

/**
 * Renders the status-update form, stages denial/query reasons in UserStore,
 * and saves status-change comments into local storage helpers. Query reasons
 * with `should_show_user_input` of `"optional"` or `"required"` toggle the
 * preferred-drug section; `"required"` also blocks Save until those fields
 * are filled.
 *
 * @input props - Modal callbacks and optional CPT row context
 * @return JSX for the Status Update modal
 */
export const StatusUpdateModal = (props: StatusUpdateModalProps) => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const { data: queryReasons } = useSelector(
    (state: RootState) => state.queryReasons,
  );
  const {
    reason,
    setReason,
    textareaContent,
    setTextareaContent,
    preferredDrugForm,
    setPreferredDrugValues,
    setStepTherapyCount,
    setSource,
    handlePreferredJcodeEntriesChange,
    isAllRequiredFieldsHaveValue,
  } = useStatusUpdateFormState();
  const { metaData } = useSelector((state: RootState) => state.modal);
  // Reserved for when remote bucket save is re-enabled on modal save.
  const isSaving = false;
  // const { singleMedicalPaOrder } = useSelector(
  //   (state: RootState) => state.singleMedicalPaOrder,
  // );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const handleClose = () => {
    props.onClose();
  };

  /**
   * Stages denial/query reasons in UserStore, then persists the status-change
   * comment via the local-storage helper.
   *
   * @return Promise that resolves after a successful save, or early when
   *   validation fails
   */
  const handleSave = async () => {
    if (
      !generalOrderDetails?.order?.order_id ||
      !reason ||
      !metaData?.commentType ||
      !user?.id
    ) {
      return;
    }
    const commentType = metaData?.commentType as
      "denial" | "query" | "query_resolution";
    const orderId = generalOrderDetails.order.order_id;

    if (commentType === "denial" || commentType === "query") {
      upsertDenialQueryReasonInStore({
        order_id: orderId,
        j_code_id: props?.rowData?.jCodeId ?? props?.rowData?.jCodes?.id ?? "",
        reason,
        type: commentType,
      });
    }

    createDataForCommentsAndPushToLocalStorage(
      orderId,
      textareaContent.trim(),
      reason,
      props?.rowData?.jCodes?.data ?? "",
      commentType,
      user?.email ?? "",
      preferredDrugForm,
    );
    props.onSave();
  };

  const isFormValid = reason && textareaContent.trim().length > 0;
  const shouldShowAdditionalInformation = useMemo(
    () => shouldShowAdditionalInformationForQueryReason(reason, queryReasons),
    [reason, queryReasons],
  );
  const isUserInputRequiredInForm = useMemo(
    () => isUserInputRequired(reason, queryReasons),
    [reason, queryReasons],
  );

  /**
   * Disables Save when the base form is incomplete, a save is in flight, or
   * preferred-drug fields are required but not yet filled.
   *
   * @returns `true` when the Save button should be disabled
   */
  const disableSaveButton = useMemo(() => {
    const missingRequiredUserInput =
      isUserInputRequiredInForm && !isAllRequiredFieldsHaveValue;
    return !isFormValid || isSaving || missingRequiredUserInput;
  }, [
    isFormValid,
    isSaving,
    isUserInputRequiredInForm,
    isAllRequiredFieldsHaveValue,
  ]);

  return (
    <Modal
      dialogId={ModalId.STATUS_UPDATE_MODAL}
      title="Status Update"
      onClose={handleClose}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving..." : "Save"}
      cancelText="Cancel"
      disableSave={disableSaveButton}
      hideFooter={false}
      maxHeightPercentage={70}
    >
      <StatusUpdateModalBody
        reason={reason}
        setReason={setReason}
        textareaContent={textareaContent}
        setTextareaContent={setTextareaContent}
        preferredDrugForm={preferredDrugForm}
        onPreferredJcodeEntriesChange={handlePreferredJcodeEntriesChange}
        setPreferredDrugValues={setPreferredDrugValues}
        setStepTherapyCount={setStepTherapyCount}
        setSource={setSource}
        metaData={metaData}
        shouldShowAdditionalInformation={shouldShowAdditionalInformation}
        isUserInputRequired={isUserInputRequiredInForm}
      />
    </Modal>
  );
};
